/**
 * Notepad — Windows 11-style single-document editor.
 *
 * Top bar  : File / Edit / View menu (each is a small dropdown)
 * Toolbar  : H1 ▾ · • ▾ · B · I · 🔗 · ⎕▾ · clear-formatting
 * Editor   : large contentEditable surface (rich text via execCommand)
 * Status   : Ln X, Col Y | N characters | Plain text | 100% | Windows (CRLF) | UTF-8
 *
 * Persists server-side via GET/PUT /api/notepad (HTML body, max 200k chars).
 * Auto-saves 600ms after the last edit; debounced. Saves are skipped on the
 * initial load (no redundant PUT).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import {
  TextB, TextItalic, Link as LinkIcon, ListBullets, ListNumbers,
  TextAa, CaretDown, Table, Eraser, Notebook, ClockCounterClockwise, X,
} from "@phosphor-icons/react";

const HEADINGS = [
  { id: "H1", value: "h1", label: "Heading 1" },
  { id: "H2", value: "h2", label: "Heading 2" },
  { id: "H3", value: "h3", label: "Heading 3" },
  { id: "P",  value: "p",  label: "Paragraph" },
];

export default function Notepad() {
  const editorRef = useRef(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // "file" | "edit" | "view" | "h" | "list" | "table"
  const [zoom, setZoom] = useState(100);
  const [meta, setMeta] = useState({ ln: 1, col: 1, chars: 0 });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);

  // ---------- initial fetch -------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notepad");
        if (editorRef.current) {
          // Sanitize server-provided HTML before mounting into a contentEditable
          // to prevent stored-XSS if the notepad body is ever tampered with.
          editorRef.current.innerHTML = DOMPurify.sanitize(data.body || "", {
            USE_PROFILES: { html: true },
          });
        }
        setSavedAt(data.updated_at);
        recomputeMeta();
      } catch (_e) {
        /* owner/guest — leave blank */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---------- close any open menu on outside click --------------------------
  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-menu]")) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ---------- auto-save -----------------------------------------------------
  const save = useCallback(async () => {
    if (!editorRef.current) return;
    const body = DOMPurify.sanitize(editorRef.current.innerHTML || "", {
      USE_PROFILES: { html: true },
    });
    setSaving(true);
    try {
      const { data } = await api.put("/notepad", { body });
      setSavedAt(data.updated_at);
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    if (loading || !dirty) return undefined;
    const t = setTimeout(() => { save(); }, 600);
    return () => clearTimeout(t);
  }, [dirty, loading, save]);

  // ---------- caret position + character count -----------------------------
  const recomputeMeta = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const text = el.innerText || "";
    const sel = window.getSelection();
    let ln = 1; let col = 1;
    if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
      // walk text up to the caret and count newlines
      const range = sel.getRangeAt(0).cloneRange();
      range.setStart(el, 0);
      const upto = range.toString();
      const lines = upto.split("\n");
      ln = lines.length;
      col = lines[lines.length - 1].length + 1;
    }
    setMeta({ ln, col, chars: text.length });
  }, []);

  // ---------- contenteditable handlers --------------------------------------
  const onInput = () => { setDirty(true); recomputeMeta(); };
  const onKeyUp = () => recomputeMeta();
  const onMouseUp = () => recomputeMeta();

  // ---------- formatting ops ------------------------------------------------
  const exec = (cmd, value) => {
    editorRef.current?.focus();
    try { document.execCommand(cmd, false, value); } catch (_e) { /* execCommand best-effort */ }
    setDirty(true);
    recomputeMeta();
  };
  const applyHeading = (tag) => exec("formatBlock", tag.toUpperCase());
  const insertLink = () => {
    const url = window.prompt("Enter URL");
    if (!url) return;
    exec("createLink", url);
  };
  const insertTable = (rows, cols) => {
    const tbl = [];
    tbl.push('<table style="border-collapse:collapse;margin:8px 0;">');
    for (let r = 0; r < rows; r++) {
      tbl.push("<tr>");
      for (let c = 0; c < cols; c++) {
        tbl.push('<td style="border:1px solid #ccc;padding:6px 10px;min-width:60px;">&nbsp;</td>');
      }
      tbl.push("</tr>");
    }
    tbl.push("</table><p></p>");
    exec("insertHTML", tbl.join(""));
  };
  const clearFormatting = () => { exec("removeFormat"); exec("formatBlock", "P"); };

  // ---------- File menu ops -------------------------------------------------
  const newDoc = () => {
    if (!window.confirm("Clear the current notepad?")) return;
    if (editorRef.current) editorRef.current.innerHTML = "";
    setDirty(true);
    recomputeMeta();
  };
  const downloadTxt = () => {
    const text = editorRef.current?.innerText || "";
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `notepad-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(editorRef.current?.innerText || "");
    } catch {
      const { toast } = await import("sonner");
      toast.error("Copy failed — clipboard access blocked.");
    }
  };
  const findInDoc = () => {
    const q = window.prompt("Find");
    if (!q) return;
    const idx = (editorRef.current?.innerText || "").indexOf(q);
    if (idx < 0) window.alert("Not found.");
  };

  // ---------- Version history ------------------------------------------------
  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryBusy(true);
    try {
      const { data } = await api.get("/notepad/history");
      setHistory(data.items || []);
    } catch {
      setHistory([]);
    } finally { setHistoryBusy(false); }
  }, []);

  const restoreSnapshot = useCallback(async (snapshotId) => {
    if (!window.confirm("Replace the current notepad with this snapshot? Your current text will itself be saved as a snapshot so you can undo this.")) return;
    setHistoryBusy(true);
    try {
      const { data } = await api.post(`/notepad/history/${snapshotId}/restore`);
      if (editorRef.current) {
        editorRef.current.innerHTML = DOMPurify.sanitize(data.body || "", {
          USE_PROFILES: { html: true },
        });
      }
      setSavedAt(data.updated_at);
      setDirty(false);
      recomputeMeta();
      setHistoryOpen(false);
      const { toast } = await import("sonner");
      toast.success("Snapshot restored");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setHistoryBusy(false); }
  }, [recomputeMeta]);

  if (loading) return <div className="p-8 text-sm text-[var(--text-muted)]" data-testid="notepad-loading">Loading…</div>;

  return (
    <div className="flex flex-col h-full bg-[#f9f9f9]" data-testid="notepad-panel">
      {/* ============= Title bar =================================================== */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border)] bg-white">
        <Notebook size={14} weight="duotone" className="text-[var(--accent)]" />
        <span className="text-xs font-medium text-[var(--text-primary)]">Untitled — Notepad</span>
        <button
          onClick={openHistory}
          className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elev)]"
          title="View up to 20 recent snapshots"
          data-testid="notepad-history-btn"
        >
          <ClockCounterClockwise size={11} /> History
        </button>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]" data-testid="notepad-status">
          {saving ? "Saving…" : savedAt ? `Saved ${timeAgo(savedAt)}` : "Not saved"}
        </span>
      </div>

      {/* ============= Menu bar (File / Edit / View) =============================== */}
      <div className="flex items-center gap-1 px-2 py-1 border-b border-[var(--border)] bg-white text-sm">
        <Menu name="File" open={openMenu === "file"} onToggle={() => setOpenMenu(openMenu === "file" ? null : "file")} testid="notepad-menu-file">
          <MenuItem onClick={() => { newDoc(); setOpenMenu(null); }} testid="notepad-menu-file-new">New</MenuItem>
          <MenuItem onClick={() => { save(); setOpenMenu(null); }} testid="notepad-menu-file-save">Save now</MenuItem>
          <MenuItem onClick={() => { downloadTxt(); setOpenMenu(null); }} testid="notepad-menu-file-download">Download .txt</MenuItem>
          <MenuItem onClick={() => { openHistory(); setOpenMenu(null); }} testid="notepad-menu-file-history">Version history…</MenuItem>
        </Menu>
        <Menu name="Edit" open={openMenu === "edit"} onToggle={() => setOpenMenu(openMenu === "edit" ? null : "edit")} testid="notepad-menu-edit">
          <MenuItem onClick={() => { exec("undo"); setOpenMenu(null); }} testid="notepad-menu-edit-undo">Undo</MenuItem>
          <MenuItem onClick={() => { exec("redo"); setOpenMenu(null); }} testid="notepad-menu-edit-redo">Redo</MenuItem>
          <MenuItem onClick={() => { copyAll(); setOpenMenu(null); }} testid="notepad-menu-edit-copy">Copy all</MenuItem>
          <MenuItem onClick={() => { findInDoc(); setOpenMenu(null); }} testid="notepad-menu-edit-find">Find…</MenuItem>
        </Menu>
        <Menu name="View" open={openMenu === "view"} onToggle={() => setOpenMenu(openMenu === "view" ? null : "view")} testid="notepad-menu-view">
          <MenuItem onClick={() => { setZoom((z) => Math.min(200, z + 10)); setOpenMenu(null); }} testid="notepad-menu-view-zoom-in">Zoom in</MenuItem>
          <MenuItem onClick={() => { setZoom((z) => Math.max(60, z - 10)); setOpenMenu(null); }} testid="notepad-menu-view-zoom-out">Zoom out</MenuItem>
          <MenuItem onClick={() => { setZoom(100); setOpenMenu(null); }} testid="notepad-menu-view-zoom-reset">Reset zoom</MenuItem>
        </Menu>
      </div>

      {/* ============= Formatting toolbar ========================================= */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-[var(--border)] bg-white">
        {/* H1 dropdown */}
        <button
          data-menu
          onClick={() => setOpenMenu(openMenu === "h" ? null : "h")}
          className="relative flex items-center gap-1 px-2 py-1 text-sm hover:bg-[var(--bg-elev)] border border-transparent hover:border-[var(--border)]"
          data-testid="notepad-toolbar-h"
        >
          <TextAa size={14} /> H1 <CaretDown size={10} />
          {openMenu === "h" && (
            <div className="absolute top-full left-0 z-20 mt-1 bg-white border border-[var(--border)] shadow-lg min-w-[140px]" data-menu>
              {HEADINGS.map((h) => (
                <button key={h.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { applyHeading(h.value); setOpenMenu(null); }}
                        className="block w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-elev)]" data-testid={`notepad-toolbar-h-${h.id.toLowerCase()}`}>
                  {h.label}
                </button>
              ))}
            </div>
          )}
        </button>

        {/* List dropdown */}
        <button data-menu onClick={() => setOpenMenu(openMenu === "list" ? null : "list")}
                className="relative flex items-center gap-1 px-2 py-1 text-sm hover:bg-[var(--bg-elev)] border border-transparent hover:border-[var(--border)]"
                data-testid="notepad-toolbar-list">
          <ListBullets size={14} /> <CaretDown size={10} />
          {openMenu === "list" && (
            <div className="absolute top-full left-0 z-20 mt-1 bg-white border border-[var(--border)] shadow-lg min-w-[160px]" data-menu>
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => { exec("insertUnorderedList"); setOpenMenu(null); }} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-[var(--bg-elev)]" data-testid="notepad-toolbar-list-ul"><ListBullets size={12} /> Bulleted list</button>
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => { exec("insertOrderedList"); setOpenMenu(null); }} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-[var(--bg-elev)]" data-testid="notepad-toolbar-list-ol"><ListNumbers size={12} /> Numbered list</button>
            </div>
          )}
        </button>

        <div className="w-px h-5 bg-[var(--border)] mx-1" />

        <button onClick={() => exec("bold")}   className="p-1.5 hover:bg-[var(--bg-elev)]" title="Bold (Ctrl+B)"   data-testid="notepad-toolbar-bold"><TextB     size={14} weight="bold" /></button>
        <button onClick={() => exec("italic")} className="p-1.5 hover:bg-[var(--bg-elev)]" title="Italic (Ctrl+I)" data-testid="notepad-toolbar-italic"><TextItalic size={14} /></button>
        <button onClick={insertLink}           className="p-1.5 hover:bg-[var(--bg-elev)]" title="Insert link"     data-testid="notepad-toolbar-link"><LinkIcon  size={14} /></button>

        {/* Table dropdown */}
        <button data-menu onClick={() => setOpenMenu(openMenu === "table" ? null : "table")}
                className="relative flex items-center gap-1 p-1.5 hover:bg-[var(--bg-elev)]"
                data-testid="notepad-toolbar-table">
          <Table size={14} /> <CaretDown size={10} />
          {openMenu === "table" && (
            <div className="absolute top-full left-0 z-20 mt-1 bg-white border border-[var(--border)] shadow-lg p-2" data-menu>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Insert table</div>
              <div className="grid grid-cols-3 gap-1">
                {[[2,2],[3,3],[4,4]].map(([r, c]) => (
                  <button key={`${r}x${c}`} onMouseDown={(e) => e.preventDefault()} onClick={() => { insertTable(r, c); setOpenMenu(null); }}
                          className="px-2 py-1 text-xs border border-[var(--border)] hover:bg-[var(--bg-elev)]"
                          data-testid={`notepad-toolbar-table-${r}x${c}`}>
                    {r}×{c}
                  </button>
                ))}
              </div>
            </div>
          )}
        </button>

        <button onClick={clearFormatting} className="p-1.5 hover:bg-[var(--bg-elev)]" title="Clear formatting" data-testid="notepad-toolbar-clear"><Eraser size={14} /></button>
      </div>

      {/* ============= Editor surface ============================================= */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onKeyUp={onKeyUp}
        onMouseUp={onMouseUp}
        className="flex-1 overflow-y-auto px-8 py-6 bg-white outline-none text-[var(--text-primary)] leading-relaxed"
        style={{ fontSize: `${zoom}%`, fontFamily: "ui-sans-serif, system-ui" }}
        data-testid="notepad-editor"
      />

      {/* ============= Status bar ================================================= */}
      <div className="grid grid-cols-6 px-3 py-1 border-t border-[var(--border)] bg-white text-[10px] font-mono text-[var(--text-muted)]">
        <span data-testid="notepad-status-pos">Ln {meta.ln}, Col {meta.col}</span>
        <span data-testid="notepad-status-chars">{meta.chars} characters</span>
        <span>Rich text</span>
        <span data-testid="notepad-status-zoom">{zoom}%</span>
        <span>Windows (CRLF)</span>
        <span className="text-right">UTF-8</span>
      </div>

      {historyOpen && (
        <HistoryModal
          items={history}
          busy={historyBusy}
          onClose={() => setHistoryOpen(false)}
          onRestore={restoreSnapshot}
        />
      )}
    </div>
  );
}

// ---------- helpers ---------------------------------------------------------

function Menu({ name, open, onToggle, testid, children }) {
  return (
    <div className="relative" data-menu>
      <button onClick={onToggle} className={`px-2.5 py-1 text-sm hover:bg-[var(--bg-elev)] ${open ? "bg-[var(--bg-elev)]" : ""}`} data-testid={testid}>
        {name}
      </button>
      {open && (
        <div className="absolute top-full left-0 z-20 mt-1 bg-white border border-[var(--border)] shadow-lg min-w-[160px]" data-menu>
          {children}
        </div>
      )}
    </div>
  );
}

function MenuItem({ children, onClick, testid }) {
  return (
    <button onMouseDown={(e) => e.preventDefault()} onClick={onClick} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-elev)]" data-testid={testid}>
      {children}
    </button>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function HistoryModal({ items, busy, onClose, onRestore }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="notepad-history-modal" role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-lg max-h-[80vh] flex flex-col shadow-xl border border-[var(--border)]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={16} className="text-[var(--accent)]" />
            <span className="text-sm font-semibold">Version history</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
              last {items.length}/20 snapshots
            </span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-elev)]" data-testid="notepad-history-close" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {busy && items.length === 0 && (
            <div className="p-6 text-sm text-[var(--text-muted)]" data-testid="notepad-history-loading">Loading…</div>
          )}
          {!busy && items.length === 0 && (
            <div className="p-6 text-sm text-[var(--text-muted)]" data-testid="notepad-history-empty">
              No snapshots yet. Snapshots are created automatically whenever the notepad changes.
            </div>
          )}
          {items.length > 0 && (
            <ul className="divide-y divide-[var(--border)]">
              {items.map((snap, i) => (
                <li key={snap.id} className="flex items-start gap-3 p-4 hover:bg-[var(--bg-elev)]" data-testid={`notepad-history-item-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                      {new Date(snap.saved_at).toLocaleString()}
                    </div>
                    <div className="mt-1 text-sm text-[var(--text-primary)] truncate" title={snap.preview}>
                      {snap.preview || <span className="italic text-[var(--text-muted)]">(empty)</span>}
                    </div>
                    <div className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">
                      {snap.size} chars
                    </div>
                  </div>
                  <button
                    onClick={() => onRestore(snap.id)}
                    disabled={busy}
                    className="shrink-0 px-3 py-1.5 text-xs font-semibold text-black bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50"
                    data-testid={`notepad-history-restore-${i}`}
                  >
                    Restore
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-4 py-2 border-t border-[var(--border)] text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
          Restoring saves the current notepad as a new snapshot first.
        </div>
      </div>
    </div>
  );
}
